import React, { useMemo, useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Typography,
  Box,
} from '@mui/material';
import FolderOutlinedIcon from '@mui/icons-material/FolderOutlined';
import HomeOutlinedIcon from '@mui/icons-material/HomeOutlined';

/**
 * Pick destination folder for move. `topLevelFolders` + `subfolders` are flat API lists (no nested children in payload).
 */
export default function MoveFolderDialog({
  open,
  onClose,
  fileName,
  topLevelFolders,
  subfolders,
  onSelect,
}) {
  const [picked, setPicked] = useState(null);
  const merged = useMemo(() => {
    const subs = Array.isArray(subfolders) ? subfolders : [];
    const tops = Array.isArray(topLevelFolders) ? topLevelFolders : [];
    const byId = new Map();
    subs.forEach((f) => byId.set(f.id, { ...f, depth: 1 }));
    tops.forEach((f) => {
      if (!byId.has(f.id)) byId.set(f.id, { ...f, depth: 0 });
    });
    return Array.from(byId.values());
  }, [subfolders, topLevelFolders]);

  const handleConfirm = () => {
    onSelect?.(picked === 'root' ? null : picked);
    setPicked(null);
    onClose?.();
  };

  return (
    <Dialog open={open} onClose={() => { setPicked(null); onClose?.(); }} maxWidth="sm" fullWidth>
      <DialogTitle>Move {fileName ? `“${fileName}”` : 'file'}</DialogTitle>
      <DialogContent dividers>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
          Choose a folder. Root keeps the file at the top level of this client.
        </Typography>
        <List dense disablePadding>
          <ListItemButton selected={picked === 'root'} onClick={() => setPicked('root')}>
            <ListItemIcon sx={{ minWidth: 36 }}>
              <HomeOutlinedIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText primary="All files (root)" />
          </ListItemButton>
          {merged.map((f) => (
            <ListItemButton
              key={f.id}
              selected={picked === f.id}
              onClick={() => setPicked(f.id)}
              sx={{ pl: 2 + (f.depth || 0) * 2.5 }}
            >
              <ListItemIcon sx={{ minWidth: 36 }}>
                <FolderOutlinedIcon fontSize="small" color="primary" />
              </ListItemIcon>
              <ListItemText primary={f.name} />
            </ListItemButton>
          ))}
        </List>
        {merged.length === 0 && (
          <Box sx={{ py: 2, textAlign: 'center' }}>
            <Typography variant="body2" color="text.secondary">
              No folders yet. Create one from the toolbar.
            </Typography>
          </Box>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={() => { setPicked(null); onClose?.(); }}>Cancel</Button>
        <Button variant="contained" onClick={handleConfirm} disabled={picked === null}>
          Move here
        </Button>
      </DialogActions>
    </Dialog>
  );
}
